import moment from 'moment'
import { parseCountry } from './favourite'
import { isInvalidPlace } from 'utils/validators/googlePlace'

export const initialFormDeliveryValues = {
  search: null,
  t1: true,
  t2: true,
  t3: true,
  today: true,
  tomorrow: true,
  after_tomorrow: true,
}

export const parseDateTime = (date, time) =>
  moment(date)
    .hours(time.split(':').shift())
    .minutes(time.split(':').pop())
    .seconds(0)
    .milliseconds(0)
    .toISOString(true)

export function parseDeliveryPayload(payload) {
  return {
    ...payload,
    merchandise: payload.merchandise.map(({ quantity, ...merchandise }) => ({
      ...merchandise,
      width: parseInt(merchandise.width),
      height: parseInt(merchandise.height),
      depth: parseInt(merchandise.depth),
      weight: parseFloat(merchandise.weight),
      ...(merchandise.type !== 'bulk' ? { quantity: parseInt(quantity) } : {}),
    })),
    transportRules: Object.keys(payload.transportRules || {}).filter((key) => payload.transportRules[key] === true),
    options: Object.keys(payload.options || {}).filter((key) => payload.options[key] === true),
    pickupWaypoints: payload.pickupWaypoints.map(
      ({
        date,
        fromTime,
        untilTime,
        address,
        additionalAddress,
        googlePlaceId,
        contactName,
        schedule,
        fromDateTime,
        untilDateTime,
        ...pickupWaypoint
      }) => ({
        address,
        additionalAddress,
        googlePlaceId: !isInvalidPlace(googlePlaceId) ? googlePlaceId : undefined,
        schedule: schedule ? 'asap' : 'between',
        fromDatetime: fromDateTime ?? parseDateTime(date, fromTime),
        untilDatetime: untilDateTime ?? parseDateTime(date, untilTime),
        estimatedHandlingDuration: parseInt(pickupWaypoint.estimatedHandlingDuration) * 60,
        contactName,
      }),
    ),

    receiptWaypoints: payload.receiptWaypoints.map(
      ({
        date,
        fromTime,
        untilTime,
        address,
        additionalAddress,
        googlePlaceId,
        contactName,
        schedule,
        fromDateTime,
        untilDateTime,
        ...receiptWaypoint
      }) => ({
        address,
        additionalAddress,
        googlePlaceId,
        schedule: schedule ? 'asap' : 'between',
        fromDatetime: fromDateTime ?? parseDateTime(date, fromTime),
        untilDatetime: untilDateTime ?? parseDateTime(date, untilTime),
        estimatedHandlingDuration: parseInt(receiptWaypoint.estimatedHandlingDuration) * 60,
        contactName,
      }),
    ),
  }
}

export function decorateDeliveryEntity(entity, state = {}) {
  if (!entity) {
    return {}
  } else {
    const roles = []

    if ((entity.freightForwarder || {}).id === state.profile.payload.id) {
      roles.push('freightForwarder')
    }

    if (
      (entity.companyCourier || { courierOfCompany: {} }).courierOfCompany.id ===
      (state.profile.payload.managedCompany || {}).id
    ) {
      roles.push('courier')
    }

    return {
      ...entity,
      estimatedPrice: entity.estimatedPrice / 100,
      adjustedPrice: entity.adjustedPrice / 100,
      name: entity.name,
      roles,
    }
  }
}

export function decorateDeliveryResponsentity(entity) {
  return {
    ...entity,
    options: entity.options.reduce((previous, key) => ({ ...previous, [key]: true }), {}),
    transportRules: entity.transportRules.reduce((previous, key) => ({ ...previous, [key]: true }), {}),
    ...(entity.freightForwarders && {
      freightForwarders: entity.freightForwarders.map((item) => ({
        label: `${item.firstname} ${item.lastname} - ${item.managedCompany ? item.managedCompany.name : ''}`,
        value: item['@id'],
      })),
    }),
    merchandise: entity.merchandise.map(({ '@type': _, ...entity }) => entity),
    receiptWaypoints: entity.receiptWaypoints.map((entity) => ({
      address: entity.address,
      additionalAddress: entity.additionalAddress,
      country: parseCountry(entity.country),
      fromTime: moment(entity.fromDatetime).format('HH:mm'),
      untilTime: moment(entity.untilDatetime).format('HH:mm'),
      schedule: entity.schedule === 'asap',
      date: new Date(entity.untilDatetime),
      googlePlaceId: entity.googlePlaceId,
      estimatedHandlingDuration: entity.estimatedHandlingDuration / 60,
    })),
    pickupWaypoints: entity.pickupWaypoints.map((entity) => ({
      address: entity.address,
      additionalAddress: entity.additionalAddress,
      country: parseCountry(entity.country),
      fromTime: moment(entity.fromDatetime).format('HH:mm'),
      untilTime: moment(entity.untilDatetime).format('HH:mm'),
      schedule: entity.schedule === 'asap',
      date: new Date(entity.untilDatetime),
      googlePlaceId: entity.googlePlaceId,
      estimatedHandlingDuration: entity.estimatedHandlingDuration / 60,
    })),
    customDuration: parseInt(entity.customDuration) / 60,
    plannedDuration: parseInt(entity.plannedDuration) / 60,
  }
}

export function parseDeliveryUpdatePayload(payload) {
  const { dirtyValues } = payload
  return {
    ...(dirtyValues.includes('tender') && {
      tender: payload.tender,
    }),
    ...(dirtyValues.includes('receiptComment') && {
      receiptComment: payload.receiptComment,
    }),
    ...(dirtyValues.includes('vehicleLoadType') && {
      vehicleLoadType: payload.vehicleLoadType,
    }),
    ...(dirtyValues.includes('merchandise') && {
      merchandise: payload.merchandise.map(({ quantity, ...merchandise }) => ({
        ...merchandise,
        width: parseInt(merchandise.width),
        height: parseInt(merchandise.height),
        depth: parseInt(merchandise.depth),
        weight: parseFloat(merchandise.weight),
        ...(merchandise.type !== 'bulk' ? { quantity: parseInt(quantity) } : {}),
      })),
    }),
    ...(dirtyValues.includes('transportRules') && {
      transportRules: Object.keys(payload.transportRules || {}).filter((key) => payload.transportRules[key] === true),
    }),
    ...(dirtyValues.includes('proposalType') && {
      proposalType: payload.proposalType,
    }),
    ...(dirtyValues.confirmation && {
      confirmation: payload.confirmation,
    }),
    ...(dirtyValues.includes('options') && {
      options: Object.keys(payload.options || {}).filter((key) => payload.options[key] === true),
    }),
    ...(dirtyValues.includes('options') && {
      options: Object.keys(payload.options || {}).filter((key) => payload.options[key] === true),
    }),
    ...(dirtyValues.includes('estimatedDurationValue') && {
      estimatedDurationValue: payload.estimatedDurationValue,
    }),
    ...(dirtyValues.includes('customDuration') && {
      customDuration: parseInt(payload.customDuration) * 60,
    }),
    ...(dirtyValues.includes('plannedDuration') && {
      plannedDuration: parseInt(payload.plannedDuration) * 60,
    }),
    ...(dirtyValues.includes('pickupWaypoints') && {
      pickupWaypoints: payload.pickupWaypoints.map(
        ({
          date,
          fromTime,
          untilTime,
          address,
          contactName,
          additionalAddress,
          schedule,
          googlePlaceId,
          ...pickupWaypoint
        }) => ({
          address,
          additionalAddress,
          schedule: schedule ? 'asap' : 'between',
          fromDatetime: parseDateTime(date, fromTime),
          untilDatetime: parseDateTime(date, untilTime),
          estimatedHandlingDuration: parseInt(pickupWaypoint.estimatedHandlingDuration) * 60,
          contactName,
          googlePlaceId: !isInvalidPlace(googlePlaceId) ? googlePlaceId : undefined,
        }),
      ),
    }),
    ...(dirtyValues.includes('receiptWaypoints') && {
      receiptWaypoints: payload.receiptWaypoints.map(
        ({
          date,
          fromTime,
          untilTime,
          address,
          contactName,
          additionalAddress,
          schedule,
          googlePlaceId,
          ...receiptWaypoint
        }) => ({
          address,
          additionalAddress,
          schedule: schedule ? 'asap' : 'between',
          fromDatetime: parseDateTime(date, fromTime),
          untilDatetime: parseDateTime(date, untilTime),
          estimatedHandlingDuration: parseInt(receiptWaypoint.estimatedHandlingDuration) * 60,
          contactName,
          googlePlaceId: !isInvalidPlace(googlePlaceId) ? googlePlaceId : undefined,
        }),
      ),
    }),
    ...(payload.step === 'second' && {
      updateMessage: payload.updateMessage,
      updateMessageChat: !!payload.updateMessageChat,
      updateMessageMail: !!payload.updateMessageMail,
      source: 'deliveryUpdate',
    }),
  }
}

export function parseDeliverySplitPayload(payload) {
  const { segment1, segment2, address, googlePlaceId } = payload
  return {
    segment1: {
      comment: segment1.comment,
      receiptWaypoints: [
        {
          address: address,
          fromDatetime: parseDateTime(segment1.receiptWaypoint.date, segment1.receiptWaypoint.fromTime),
          untilDatetime: parseDateTime(segment1.receiptWaypoint.date, segment1.receiptWaypoint.untilTime),
          schedule: 'between',
          googlePlaceId,
        },
      ],
    },
    segment2: {
      comment: segment2.comment,
      pickupWaypoints: [
        {
          address: address,
          fromDatetime: parseDateTime(segment2.pickupWaypoint.date, segment2.pickupWaypoint.fromTime),
          untilDatetime: parseDateTime(segment2.pickupWaypoint.date, segment2.pickupWaypoint.untilTime),
          schedule: 'between',
          googlePlaceId,
        },
      ],
    },
  }
}
